import { AxiosError } from "axios";
import cx from "classnames";
import React, { useState } from "react";
import { useQuery } from "react-query";
import { Photo, QuestionTooltip, Shimmer, TextArea } from "component-library";

import { formatDateTime } from "shared/helpers/breakdownViewHelpers";
import { prepareHelper } from "shared/helpers/translationHelpers";

import { IPhoto } from "models/api";
import { ISuggestion } from "models/suggestion";

import profileService from "services/api/profile/profileService";
import { getImageUrl, ImageLocation } from "services/urlBuilder";

import { PhotoGallery } from "components/PhotoGalery/PhotoGallery";
import { IViewPerformerButtonProps } from "components/SuggestionsViewPerformer/SuggestionsViewPerformer";
import { IRoleSuggestionsViewTexts } from "components/SuggestionsViewPerformer/SuggestionsViewPerformer.helpers";

import "../SuggestionsViewPerformer.scss";

export interface IFullAccessPerformerProps {
  texts: IRoleSuggestionsViewTexts;
  isRoleClosed: boolean;
  suggestion?: ISuggestion;
  renderTitle: () => React.JSX.Element;
  renderButtons: (props: IViewPerformerButtonProps) => React.JSX.Element;
  onMakeSuggestion: (photoId: string, comment: string) => void;
  onDeleteSuggestion: () => void;
  onEditSuggestion: (photo: IPhoto, comment: string) => void;
  handleError: (e: AxiosError) => void;
  isPrintView?: boolean;
  isRoleCast?: boolean | null;
}

const FullAccessPerformer = ({
  suggestion,
  isRoleClosed,
  renderButtons,
  texts,
  isPrintView,
  isRoleCast,
  renderTitle,
  onMakeSuggestion,
  onEditSuggestion,
  handleError,
  onDeleteSuggestion,
}: IFullAccessPerformerProps) => {
  const [isEditMode, setIsEditMode] = useState(!suggestion);
  const [selectedPhoto, setSelectedPhoto] = useState<IPhoto | undefined>(
    undefined
  );
  const [comments, setComments] = useState(suggestion?.comment);

  const {
    data: getPhotosData = [],
    isLoading,
    refetch,
  } = useQuery<IPhoto[], AxiosError>(
    ["get-photos"],
    () => profileService.getPhotos(),
    {
      enabled: !suggestion && !isRoleClosed,
      onSuccess(data) {
        if (suggestion) {
          setSelectedPhoto(
            data.find((photo) => photo.fileName === suggestion.photo.fileName)
          );
        } else {
          setSelectedPhoto(data[0]);
        }
      },
      onError(e) {
        handleError(e);
      },
    }
  );

  if ((!suggestion && isRoleClosed) || isRoleCast) {
    return renderButtons({
      primary: {
        text: isRoleCast
          ? texts.makeSuggestionIsRoleCastButtonText
          : texts.makeSuggestionLabelActionDisabled,
        disabled: true,
        type: "primary",
      },
    });
  }

  const switchToEditMode = (flag: boolean) => {
    if (flag === false) {
      setComments(suggestion?.comment);
    }

    setIsEditMode(flag);
  };

  const renderLoadingStub = () => (
    <div className="c-suggestions-view-performer__photo-section">
      <div className="c-suggestions-view-performer__main-photo-container">
        <Shimmer />
      </div>
      <div className="c-suggestions-view-performer__stub-photo-small">
        <Shimmer />
      </div>
    </div>
  );

  const renderMainPhoto = (photoId: string) => (
    <div className="c-suggestions-view-performer__main-photo-container">
      <Photo
        className="c-suggestions-view-performer__main-photo"
        src={getImageUrl(photoId, ImageLocation.MainPhoto)}
        hasLoadingSpinner={true}
        alt={photoId}
      />
    </div>
  );

  const handlePhotoSelect = (photo: IPhoto) => {
    setSelectedPhoto(photo);
  };

  const renderPhotoSection = () => {
    return (
      <div className="c-suggestions-view-performer__photo-section">
        {renderMainPhoto(selectedPhoto?.fileName ?? "")}
        <div className="c-suggestions-view-performer__gallery-container">
          <PhotoGallery
            photos={getPhotosData}
            selectedPhotoId={selectedPhoto?.id ?? ""}
            onSelect={handlePhotoSelect}
          />
        </div>
      </div>
    );
  };

  const textareaValueChanged = (value: string) => {
    setComments(value);
  };

  const renderEditSection = () => {
    const galleryHelperProps = prepareHelper(
      texts.makeSuggestionSelectPhotoTooltipTitle,
      texts.makeSuggestionSelectPhotoTooltipText
    );
    const textAreaHelperProps = prepareHelper(
      texts.makeSuggestionMessageTooltipTitle,
      texts.makeSuggestionMessageTooltipText
    );

    const buttonProps: IViewPerformerButtonProps = suggestion
      ? {
          secondary: {
            text: texts.makeSuggestionActionCancel,
            onClick: () => switchToEditMode(false),
            type: "secondary",
          },
          primary: {
            text: texts.makeSuggestionActionSubmit,
            onClick: () => onEditSuggestion(selectedPhoto!, comments ?? ""),
            type: "primary",
          },
        }
      : {
          primary: {
            text: texts.makeSuggestionActionSubmit,
            onClick: () => onMakeSuggestion(selectedPhoto!.id, comments ?? ""),
            type: "primary",
          },
        };

    return (
      <>
        <div className="c-suggestions-view-performer__content g-bg-default">
          {renderTitle()}
          <div className="g-inner-grid-12">
            <div className="g-col-lg-9 c-suggestions-view-performer__photo-field">
              <div className="c-input-label c-input-label--with-annex">
                {texts.makeSuggestionLabelSelectPhoto}
                <QuestionTooltip {...galleryHelperProps} />
              </div>
              {isLoading ? renderLoadingStub() : renderPhotoSection()}
            </div>
            <div className="g-col-lg-9 c-suggestions-view-performer__comments-field">
              <TextArea
                id="makeSuggestionText"
                isLoading={isLoading}
                showsCharacterCount
                maxLength={1000}
                allowsOverMaxLength={false}
                label={texts.makeSuggestionTitleSuggestionMessage}
                value={comments}
                placeholder={texts.makeSuggestionLabelMessagePlaceholder}
                valueChanged={textareaValueChanged}
                height={{ lg: 89 }}
                annex={<QuestionTooltip {...textAreaHelperProps} />}
              />
            </div>
            <div className="g-col-lg-9 c-suggestions-view-performer__note">
              {texts.makeSuggestionLabelSuggestionNote}
            </div>
          </div>
        </div>
        {renderButtons(buttonProps)}
      </>
    );
  };

  const handleMakeSuggestionClick = async () => {
    await refetch();
    switchToEditMode(true);
  };

  const renderPerformer = () => {
    const buttonProps: IViewPerformerButtonProps = isRoleClosed
      ? {
          primary: {
            text: texts.makeSuggestionLabelActionDisabled,
            disabled: true,
            type: "primary",
          },
        }
      : {
          secondary: {
            text: texts.makeSuggestionActionRemove,
            onClick: onDeleteSuggestion,
            type: "secondary",
          },
          primary: {
            text: texts.makeSuggestionActionEdit,
            onClick: handleMakeSuggestionClick,
            type: "primary",
          },
        };

    return (
      <>
        <div className="c-suggestions-view-performer__content g-bg-default">
          {renderTitle()}
          <div className="g-col-lg-12 g-inner-grid">
            <div className="c-suggestions-view-performer__suggestion_description g-col-lg-9">
              <div className="c-suggestions-view-performer__full-access-photo">
                {renderMainPhoto(suggestion!.photo.fileName ?? "")}
              </div>
              <h5 className="c-suggestions-view-performer__full-access-info c-suggestions-view-performer__full-access-info--date">
                {formatDateTime(
                  texts.makeSuggestionLabelSuggestionDetails,
                  suggestion!.dateCreated
                )}
              </h5>
              <div className="c-suggestions-view-performer__full-access-comments">
                <h5 className="c-suggestions-view-performer__full-access-info">
                  {texts.makeSuggestionLabelSuggestionMessage}
                </h5>
                {suggestion!.comment}
              </div>
            </div>
          </div>
        </div>
        {renderButtons(buttonProps)}
      </>
    );
  };

  return (
    <section
      className={cx("c-suggestions-view-performer", {
        "c-suggestions-view-performer--print": isPrintView,
      })}
    >
      {isEditMode || isLoading ? renderEditSection() : renderPerformer()}
    </section>
  );
};

export default FullAccessPerformer;
